import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import { migrateSections } from '../sections/migration';
import { generateHTML } from './generator';
import { uploadPublishedHTML } from './storage';

// Helper to migrate publishedPages sections
function migratePublishedPages(publishedPages) {
  if (!publishedPages || !Array.isArray(publishedPages)) return publishedPages;
  return publishedPages.map(page => ({
    ...page,
    sections: migrateSections(page.sections || [])
  }));
}

const COLLECTION = 'projects';

// Slug уже хранится в транслитерированном виде (см. PageSelector)
// Здесь только минимальная санитизация на случай невалидных символов
const sanitizeSlug = (slug) => {
  if (!slug) return '';
  return slug
    .toLowerCase()
    .replace(/[^a-z0-9-]/gi, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
};

// Get all projects for a user
export async function getUserProjects(userId) {
  const q = query(
    collection(db, COLLECTION),
    where('userId', '==', userId),
    orderBy('updatedAt', 'desc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));
}

// Get a single project by ID
export async function getProject(projectId) {
  const docRef = doc(db, COLLECTION, projectId);
  const docSnap = await getDoc(docRef);
  if (docSnap.exists()) {
    const data = docSnap.data();
    return {
      id: docSnap.id,
      ...data,
      publishedPages: migratePublishedPages(data.publishedPages)
    };
  }
  return null;
}

// Get a published project by subdomain
export async function getProjectBySubdomain(subdomain) {
  const q = query(
    collection(db, COLLECTION),
    where('subdomain', '==', subdomain.toLowerCase()),
    where('status', '==', 'published')
  );
  const snapshot = await getDocs(q);
  if (snapshot.empty) {
    return null;
  }
  const docSnap = snapshot.docs[0];
  const data = docSnap.data();
  return {
    id: docSnap.id,
    ...data,
    publishedPages: migratePublishedPages(data.publishedPages)
  };
}

// Create a new project
export async function createProject(userId, data) {
  const projectData = {
    userId,
    name: data.name,
    template: data.template || 'blank',
    status: 'draft',
    settings: {
      seo: {},
      domain: {},
      integrations: {},
      contactForm: {},
      social: {}
    },
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  };
  const docRef = await addDoc(collection(db, COLLECTION), projectData);
  return { id: docRef.id, ...projectData };
}

// Update a project
export async function updateProject(projectId, data) {
  const docRef = doc(db, COLLECTION, projectId);
  await updateDoc(docRef, {
    ...data,
    updatedAt: serverTimestamp()
  });
}

// Delete a project
export async function deleteProject(projectId) {
  const docRef = doc(db, COLLECTION, projectId);
  await deleteDoc(docRef);
}

// Duplicate a project (including all pages)
export async function duplicateProject(projectId, userId) {
  const original = await getProject(projectId);
  if (!original) throw new Error('Project not found');

  // 1. Create new project
  const newProject = {
    userId,
    name: `${original.name} (копия)`,
    template: original.template,
    status: 'draft',
    settings: original.settings || {},
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  };
  const newProjectRef = await addDoc(collection(db, COLLECTION), newProject);
  const newProjectId = newProjectRef.id;

  // 2. Copy all pages from original project
  const pagesRef = collection(db, COLLECTION, projectId, 'pages');
  const pagesSnapshot = await getDocs(query(pagesRef, orderBy('order', 'asc')));

  const copyPromises = pagesSnapshot.docs.map(async (pageDoc) => {
    const pageData = pageDoc.data();
    const newPagesRef = collection(db, COLLECTION, newProjectId, 'pages');
    await addDoc(newPagesRef, {
      name: pageData.name,
      slug: pageData.slug,
      sections: pageData.sections || [],
      order: pageData.order || 0,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
  });

  await Promise.all(copyPromises);

  return { id: newProjectId, ...newProject };
}

// Publish a project
export async function publishProject(projectId, publishedPages, ogImageUrl = null) {
  // 1. Получаем данные проекта для settings (тема, SEO, subdomain, customDomain)
  const project = await getProject(projectId);
  if (!project) {
    throw new Error('Проект не найден');
  }

  const settings = project.settings || {};
  const subdomain = project.subdomain;
  const customDomain = project.customDomain;

  // Нужен хотя бы subdomain или customDomain
  if (!subdomain && !customDomain) {
    throw new Error('Укажите поддомен или кастомный домен в настройках');
  }

  // Формируем URL сайта для OG тегов
  const baseDomain = import.meta.env.VITE_BASE_DOMAIN || 'yourdomain.com';
  const siteUrl = customDomain
    ? `https://${customDomain}`
    : `https://${subdomain}.${baseDomain}`;

  // Сортируем страницы по order, главная (order=0 или slug='') первая
  const sortedPages = [...publishedPages].sort((a, b) => (a.order || 0) - (b.order || 0));

  // 2. Генерируем и загружаем HTML для каждой страницы
  const uploadPromises = [];

  for (const page of sortedPages) {
    const isHomePage = page.order === 0 || page.slug === '' || page.slug === 'index';
    // Санитизируем slug для безопасного использования в путях Firebase Storage
    const safeSlug = sanitizeSlug(page.slug);
    const pagePath = isHomePage ? '' : safeSlug;
    const pageUrl = isHomePage ? siteUrl : `${siteUrl}/${safeSlug}`;

    // Генерируем HTML для страницы
    const htmlContent = generateHTML(
      { sections: page.sections || [] },
      {
        title: page.seoTitle || page.name || settings.seo?.title || project.name || 'Untitled',
        description: page.seoDescription || settings.seo?.description || '',
        favicon: settings.seo?.favicon || '',
        ogImage: ogImageUrl || '',
        siteUrl: pageUrl,
        theme: settings.theme || 'default',
        enableHydration: true,
      }
    );

    // Загружаем в Storage по subdomain и/или customDomain
    if (subdomain) {
      uploadPromises.push(uploadPublishedHTML(subdomain, htmlContent, pagePath));
    }
    if (customDomain) {
      uploadPromises.push(uploadPublishedHTML(customDomain, htmlContent, pagePath));
    }
  }

  await Promise.all(uploadPromises);

  console.log(`Published ${sortedPages.length} pages for project ${projectId}`);

  // 3. Обновляем проект в Firestore
  const docRef = doc(db, COLLECTION, projectId);
  await updateDoc(docRef, {
    status: 'published',
    publishedAt: serverTimestamp(),
    publishedPages: sortedPages, // Снэпшот страниц на момент публикации
    thumbnail: ogImageUrl || null, // URL превью для отображения в dashboard
    updatedAt: serverTimestamp()
  });
}

// Unpublish a project: removes published HTML (Storage + VPS) and sets status to draft
export async function unpublishProject(projectId) {
  const unpublishFn = httpsCallable(functions, 'unpublishSite');
  const result = await unpublishFn({ projectId });
  return result.data;
}

// Check if subdomain is available (via Cloud Function for security)
export async function checkSubdomainAvailability(subdomain, currentProjectId = null) {
  const checkFn = httpsCallable(functions, 'checkSubdomainAvailability');
  const result = await checkFn({ subdomain, projectId: currentProjectId });
  return result.data;
}

// Check if custom domain is available (via Cloud Function for security)
export async function checkCustomDomainAvailability(domain, currentProjectId = null) {
  const checkFn = httpsCallable(functions, 'checkCustomDomainAvailability');
  const result = await checkFn({ domain, projectId: currentProjectId });
  return result.data;
}

// Set custom domain for a project
export async function setCustomDomain(projectId, domain) {
  const docRef = doc(db, COLLECTION, projectId);

  if (domain) {
    await updateDoc(docRef, {
      customDomain: domain.toLowerCase(),
      updatedAt: serverTimestamp()
    });
  } else {
    // Удаляем кастомный домен
    await updateDoc(docRef, {
      customDomain: null,
      updatedAt: serverTimestamp()
    });
  }
}

// Get custom domain DNS instructions
export function getCustomDomainInstructions(domain) {
  return {
    domain,
    records: [
      {
        type: 'CNAME',
        name: domain,
        value: '730806c97ac3af2d.topology.gslb.yccdn.ru',
        description: 'Направляет домен на CDN'
      }
    ],
    note: 'После добавления DNS записей, изменения могут вступить в силу в течение 24-48 часов.'
  };
}
